import {
    Component,
    computed,
    effect,
    inject, OnDestroy,
    OnInit,
    signal,
    untracked,
    viewChildren
} from '@angular/core';
import { environment } from '@environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';

import { AuthService } from '@services/auth.service';
import { BreakpointService } from '@services/breakpoint.service';
import { MenuStateService } from '@services/menu-state.service';
import { RegistrarService } from '@services/registrar.service';
import { ScopeService } from '@services/scope.service';

import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ScopeServer } from '@models/scope-server';
import { Status } from '@models/status';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { SvgIconModule } from '@shared/svg-icon.module';
import { LaunchDialogComponent } from '../../modules/dashboard/launch-dialog/launch-dialog.component';

@Component( {
    selector: 'app-toolbar',
    imports: [
        RouterLink,
        RouterLinkActive,
        MatButtonModule,
        MatMenuModule,
        MatToolbarModule,
        MatDividerModule,
        MatIconModule,
        MatTooltipModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        DebounceClickDirective,
        NgClass,
        SvgIconModule
    ],
    templateUrl: './toolbar.component.html',
    styleUrl: './toolbar.component.scss',
    host: { '(document:click)': 'onDocumentClick($event)' }
} )
export class ToolbarComponent implements OnInit, OnDestroy {
    readonly menuTriggers = viewChildren( MatMenuTrigger );

    /** environment flags */
    readonly showStyleGuide = environment.optionalModules.showStyleGuide;
    readonly switchTenants = environment.optionalModules.switchTenants;

    #lang = 'en';

    readonly indicator = signal( '' );
    readonly color = signal( '' );
    readonly status = signal( '' );

    linkKnowledgeBase = '';

    readonly #destroyed: Subject<void> = new Subject<void>();

    authService = inject( AuthService );
    #breakpointService = inject( BreakpointService );
    #cookieService = inject( CookieService );
    #scopeService = inject( ScopeService );
    #registrarService = inject( RegistrarService );
    #menuState = inject( MenuStateService );
    #dialog = inject( MatDialog );

    readonly currentUser = this.authService.currentUser;

    readonly AVATAR_PLACEHOLDER = 'avatar_placeholder';
    readonly avatar = computed( () => this.currentUser().avatarURL ?? this.AVATAR_PLACEHOLDER );

    readonly EMPTY_STRING = '';
    readonly organizationLabel = this.#registrarService.organizationLabel;

    readonly isMobile = this.#breakpointService.isMobile;
    readonly isTabletPortrait = this.#breakpointService.isTabletPortrait;

    /** reference on scope servers, assigned to users organisation */
    readonly servers = computed<ScopeServer[] | null>( () => this.authService.servers() );
    readonly selectedServer = computed<ScopeServer | null>( () => this.authService.selectedServer() );

    constructor() {
        // Close own menus when sidenav opens one — ignore own emissions to avoid self-close
        effect( () => {
            const source = this.#menuState.menuOpened();
            if ( source && source !== 'toolbar' ) {
                this.closeAllMenus();
            }
        } );

        effect( () => {
            const scopeTenantID = this.currentUser()?.scopeTenantID;

            untracked( () => {
                if ( scopeTenantID ) {
                    this.#registrarService.getScopeTenantByID( scopeTenantID ).subscribe();
                }
            } );
        } );
    }

    /**
     * helper function to open {@link LaunchDialogComponent} modal
     * with required parameters to start Scope client
     * @returns void
     * @throws Error when no server is selected
     * @see {@link LaunchDialogComponent}
     */
    launchScope() {
        this.#dialog.open( LaunchDialogComponent, {
            data: {
                version: this.selectedServer()?.scopeVersion,
                url: this.selectedServer()?.startUrl
            }
        } );
    }

    // ── Menus ─────────────────────────────────────────────────────────────────

    closeAllMenus(): void {
        this.menuTriggers()?.forEach( ( t ) => {
            if ( t.menuOpen ) t.closeMenu();
        } );
    }

    closeOtherMenus( openTrigger: MatMenuTrigger ): void {
        this.menuTriggers().forEach( ( t ) => {
            if ( t !== openTrigger && t.menuOpen ) t.closeMenu();
        } );
    }

    /** Close all menus on any click outside the flyout panel or a menu trigger. */
    onDocumentClick( event: MouseEvent ): void {
        const target = event.target as HTMLElement;
        const inOverlay = target.closest( '.cdk-overlay-container' );
        const onTrigger = target.closest( '.toolbar__trigger' );
        if ( !inOverlay && !onTrigger ) {
            this.closeAllMenus();
        }
    }

    /** Called from (menuOpened) in template — closes siblings and notifies sidenav. */
    onMenuOpened( trigger: MatMenuTrigger ): void {
        this.closeOtherMenus( trigger );
        this.#menuState.notifyMenuOpened( 'toolbar' );
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    ngOnInit(): void {
        this.setKnownledgeBaseLinks();
    }

    checkEnvironmentName( name: string ) {
        return environment.name.startsWith( name );
    }

    login() {
        this.authService.login();
    }
    logout() {
        this.authService.logout();
    }

    setKnownledgeBaseLinks() {
        const cookieExists: boolean = this.#cookieService.check( 'language' );

        if ( cookieExists ) {
            this.#lang = this.#cookieService.get( 'language' );
        } else {
            this.#lang = navigator.language;
        }

        switch ( this.#lang ) {
            case 'de':
            case 'de-DE':
            case 'de-AT':
            case 'de-CH':
                this.linkKnowledgeBase = 'https://service.riege.com/de/knowledge';
                break;
            case 'es':
                this.linkKnowledgeBase = 'https://service.riege.com/es-mx/knowledge';
                break;
            default:
                this.linkKnowledgeBase = 'https://service.riege.com/en/knowledge';
        }
    }

    getStatus() {
        this.#scopeService
            .getStatus()
            .pipe()
            .subscribe( ( res: Status ) => {
                const state = res.status.indicator;
                let icon = 'check_circle';
                let color = 'green';
                let status = $localize`:@@toolbar-status-normal:All systems operational`;

                switch ( state ) {
                    case 'minor':
                        icon = 'remove_circle_outline';
                        color = 'yellow';
                        status = $localize`:@@toolbar-status-minor:Degraded performance`;
                        break;
                    case 'partial':
                        icon = 'error_outline';
                        color = 'red';
                        status = $localize`:@@toolbar-status-partial:Partial outage`;
                        break;
                    case 'major':
                        icon = 'report_problem';
                        color = 'red';
                        status = $localize`:@@toolbar-status-major:Major outage`;
                        break;
                    case 'maintenance':
                        icon = 'build_circle';
                        color = 'blue';
                        status = $localize`:@@toolbar-status-maintenance:Under maintenance`;
                        break;
                }

                this.indicator.set( icon );
                this.color.set( color );
                this.status.set( status );
            } );
    }

    switchTenant( scopeTID: string ) {
        console.log( 'yet not implemented to switch', scopeTID );
    }

    ngOnDestroy() {
        this.#destroyed.next();
        this.#destroyed.complete();
    }
}
